/**
 * Return just the filename given the full path of the file
 * @param path
 */
export function basename(path: string) {
  const lastIndex = path.lastIndexOf('/')
  return lastIndex == -1 ? path : path.substring(lastIndex + 1)
}

export function dirname(path: string): string {
  const lastIndex = path.lastIndexOf('/')
  return lastIndex == -1 ? path : path.substring(0, lastIndex)
}

/**
 * Return the file extension, including the '.' of a file
 * Normalized to lowercase to avoid bugs when checking for particular extensions
 *
 * If the file has no extension, returns an empty string.
 *
 * @param filename
 * @returns
 */
export function extname(filename: string): string {
  const extIndex = filename.lastIndexOf('.')
  return extIndex == -1 ? '' : filename.substring(extIndex).toLowerCase()
}

/**
 * Returns the filename without the extension.
 *
 * Example:
 *   rootname("foo.txt")    // => "foo"
 *   rootname("foo.tar.gz") // => "foo.tar"
 *   rootname("foo")        // => "foo"
 *   rootname("myfile")     // => "myfile"
 */
export function rootname(filename: string): string {
  const e = extname(filename)
  return e ? filename.slice(0, -e.length) : filename
}

/**
 * If we want a simple filename representation that is compatible for web urls,
 * Mac, and Windows, the easiest solution is to sanitize the filename and remove incompatible characters
 *
 * @param filename
 * @returns
 */
export function sanitizeFilename(filename: string): string {
  const VALID_CHARS_REGEX = /[A-Za-z0-9\-._~!$'();, ]+/g
  // Extract all matches of valid characters and join them back into a string
  // This removes any characters not included in the regex pattern
  const sanitized = (filename.match(VALID_CHARS_REGEX) || []).join('')
  return sanitized.replace(/\s+/g, ' ').trim()
}

export function isValidFilename(filename: string): boolean {
  const name = filename.trim()
  return !['', '.', '..'].includes(name) && !/[\/\\\r\n\0]/.test(name)
}

/**
 * Joins path segments for file storage
 */
export function joinPath(...segments: string[]): string {
  return segments.join('/')
}

export function dropFileExtension(filename: string): string {
  const ext = extname(filename)
  if (ext == '') return filename // nothing to drop bc no file extension
  return filename.substring(0, filename.length - 1 - ext.length)
}

export function ensureFileExtension(filename: string, ext: string): string {
  if (filename.endsWith(`.${ext}`)) {
    return filename
  }
  return `${filename}.${ext}`
}

export function formatFileSize(numBytes: number): string {
  if (numBytes < 1024) {
    return `${numBytes} B`
  }
  if (numBytes < 1024 * 1024) {
    return `${(numBytes / 1024).toFixed(2)} KB`
  }
  if (numBytes < 1024 * 1024 * 1024) {
    return `${(numBytes / 1024 / 1024).toFixed(2)} MB`
  }
  return `${(numBytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

export function isJunkFilename(filename: string): boolean {
  const name = basename(filename)

  return (
    name.startsWith('.') ||
    name.startsWith('~') ||
    filename.startsWith('__MACOSX') ||
    name == 'Thumbs.db' ||
    name == 'desktop.ini'
  )
}
